import { api } from "@/lib/api";

/**
 * Web Push wiring — desktop web and Android (Chrome honors this in a plain
 * tab; iOS Safari only once the PWA is installed to the home screen, same
 * code path either way). Not used at all if the browser lacks the APIs, so
 * every entry point here is safe to call speculatively.
 */
export function pushSupported() {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

export function pushPermission() {
  if (!pushSupported()) return "unsupported";
  return Notification.permission; // "default" | "granted" | "denied"
}

function urlBase64ToUint8Array(base64) {
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(b64);
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
}

/** The actual "make sure this browser has a live subscription registered
 * with the backend" work, shared by enablePush() (which may first need to
 * prompt for permission) and verifyPushSubscription() (which never prompts —
 * it only acts when permission is already granted). Re-registering an
 * existing subscription is a harmless, idempotent upsert server-side, so
 * it's always safe to call this even when a subscription already exists. */
async function subscribeThisBrowser() {
  const registration = await navigator.serviceWorker.ready;
  let subscription = await registration.pushManager.getSubscription();
  if (!subscription) {
    const { data } = await api.get("/push/public-key");
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(data.public_key),
    });
  }
  const json = subscription.toJSON();
  await api.post("/push/subscribe", { endpoint: json.endpoint, keys: json.keys });
}

/** Asks for permission (if not already decided), subscribes this browser to
 * push, and registers the subscription with the backend. Returns the final
 * permission string — callers show their own toast/UI off of that. */
export async function enablePush() {
  if (!pushSupported()) return "unsupported";
  let permission = Notification.permission;
  if (permission === "default") {
    permission = await Notification.requestPermission();
  }
  if (permission !== "granted") return permission;

  await subscribeThisBrowser();
  return "granted";
}

/**
 * Self-healing check, meant to run once per app load for a signed-in user —
 * never prompts, never touches the on/off toggle, just quietly makes sure a
 * browser that already said yes still has a working subscription.
 *
 * The on/off state that button shows is only ever "did this browser's
 * Notification permission get granted" — that's a one-time OS/browser-level
 * grant that basically never changes back on its own. Whether the actual
 * PushSubscription behind it is still alive is a completely separate thing,
 * and it can quietly die without permission ever changing: browser data
 * gets cleared, the push service expires an old endpoint, a profile gets
 * reset, an OS reinstalls the browser. When that happens the toggle keeps
 * showing "on" forever — there's nothing that would flip it off — and that
 * one computer just silently stops receiving anything, while every other
 * computer that happens to have a live subscription keeps working fine.
 * Calling this on every load is what catches that: if the subscription is
 * gone, it quietly recreates it (no new permission prompt needed, since
 * permission was already granted); if it's still there, it just re-asserts
 * it with the backend, which is a no-op if nothing changed.
 */
export async function verifyPushSubscription() {
  if (!pushSupported()) return;
  if (Notification.permission !== "granted") return;
  try {
    await subscribeThisBrowser();
  } catch {
    // Best-effort — if this fails (offline, server hiccup, tenant not
    // chosen yet for an owner), this computer just keeps whatever
    // subscription it already had and we try again next load.
  }
}

export async function disablePush() {
  if (!pushSupported()) return;
  const registration = await navigator.serviceWorker.ready;
  const subscription = await registration.pushManager.getSubscription();
  if (!subscription) return;
  const endpoint = subscription.endpoint;
  await subscription.unsubscribe().catch(() => {});
  await api.post("/push/unsubscribe", { endpoint }).catch(() => {});
}
